import React from "react";
import { Text, Stack, Group, Box } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { ProgressiveStatus } from "../types/onboarding";
import { ProgressiveStep } from "./ProgressiveWizardShell";
import styles from "./ProgressiveSidebar.module.css";

interface ProgressiveSidebarProps {
  currentStep: ProgressiveStep;
  employeesStatus: ProgressiveStatus;
  servicesStatus: ProgressiveStatus;
  paymentsStatus: ProgressiveStatus;
  onStepClick?: (step: ProgressiveStep) => void;
}

interface StepItemProps {
  label: string;
  stepNumber: number;
  status: ProgressiveStatus;
  isActive: boolean;
  onClick?: () => void;
}

function StepItem({
  label,
  stepNumber,
  status,
  isActive,
  onClick,
}: StepItemProps) {
  const isComplete = status === "complete";
  const isClickable = !!onClick;

  return (
    <Group
      gap="sm"
      align="center"
      className={`${styles.stepItem} ${isActive ? styles.stepItemActive : ""} ${isClickable ? styles.stepItemClickable : ""}`}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                onClick?.();
              }
            }
          : undefined
      }
    >
      <Box
        className={`${styles.stepIndicator} ${isComplete ? styles.stepIndicatorComplete : ""} ${isActive && !isComplete ? styles.stepIndicatorActive : ""} ${!isActive && !isComplete ? styles.stepIndicatorInactive : ""}`}
      >
        {isComplete ? (
          <IconCheck size={16} stroke={2.5} className={styles.checkIcon} />
        ) : (
          <Text size="sm" fw={600} className={styles.stepNumber}>
            {stepNumber}
          </Text>
        )}
      </Box>
      <Text
        size="sm"
        fw={400}
        className={`${isComplete ? styles.stepLabelComplete : ""} ${isActive && !isComplete ? styles.stepLabelActive : ""} ${!isActive && !isComplete ? styles.stepLabelInactive : ""}`}
      >
        {label}
      </Text>
    </Group>
  );
}

export default function ProgressiveSidebar({
  currentStep,
  employeesStatus,
  servicesStatus,
  paymentsStatus,
  onStepClick,
}: ProgressiveSidebarProps) {
  const steps: Array<{
    key: ProgressiveStep;
    label: string;
    stepNumber: number;
    status: ProgressiveStatus;
  }> = [
    {
      key: "employees",
      label: "Add Employees",
      stepNumber: 1,
      status: employeesStatus,
    },
    {
      key: "services",
      label: "Add Services",
      stepNumber: 2,
      status: servicesStatus,
    },
    {
      key: "payments",
      label: "Add Payments",
      stepNumber: 3,
      status: paymentsStatus,
    },
  ];

  return (
    <Stack gap="xs">
      <Text fw={700} size="lg" c="#101820">
        Setup
      </Text>
      <Text size="sm" c="dimmed">
        Get up and running in less than 20 minutes.
      </Text>

      <Stack gap="md" mt="xl">
        {steps.map((step, index) => (
          <StepItem
            key={step.key}
            label={step.label}
            stepNumber={step.stepNumber}
            status={step.status}
            isActive={currentStep === step.key}
            onClick={
              onStepClick && currentStep !== step.key
                ? () => onStepClick(step.key)
                : undefined
            }
          />
        ))}
      </Stack>
    </Stack>
  );
}
